import { ensureValue } from '@rematter/ui-web';
import { atom } from 'jotai';

import { userCreateMutationAtom } from '../_graphql/mutationUserCreate';
import { addUserToOrganizationMutationAtom } from '../_graphql/mutationUserOrganization';
import { userUpdateMutationAtom } from '../_graphql/mutationUserUpdate';

import { $users } from './userList.state';

// 🔘🔘🔘 TYPES
export type UserDialogMode = 'create' | 'edit';

export type UserFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  timezone?: string;
  idpId?: string;
  organizationId?: string;
};

// 🔘🔘🔘 INITIAL STATE
const getInitialFormData = (): UserFormData => ({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: undefined,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  idpId: undefined,
  organizationId: undefined,
});

// 🔘🔘🔘 PLAIN ATOMS
export const $userDialogOpen = atom(false);
export const $userDialogMode = atom<UserDialogMode>('create');
export const $userDialogId = atom<string | null>(null);
export const $userFormData = atom<UserFormData>(getInitialFormData());

// 🟡🟡🟡 HIDDEN ACTION -> CREATE user
export const $createUser = userCreateMutationAtom();

// 🟡🟡🟡 HIDDEN ACTION -> ADD user to organization
export const $addUserToOrganization = addUserToOrganizationMutationAtom();

// 🟡🟡🟡 HIDDEN ACTION -> UPDATE user
export const $updateUser = userUpdateMutationAtom({
  onSuccess: (get, set) => {
    get($users).refetch();
    set($userDialogOpen, false);
    set($userFormData, getInitialFormData());
  },
});

// 🟠🟠🟠 ACTION -> OPEN dialog for create
export const $openCreateUserDialog = atom(null, (_get, set) => {
  set($userDialogMode, 'create');
  set($userDialogId, null);
  set($userFormData, getInitialFormData());
  set($userDialogOpen, true);
});

// 🟠🟠🟠 ACTION -> OPEN dialog for edit
export const $openEditUserDialog = atom(
  null,
  (
    _get,
    set,
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string | null;
      timezone?: string | null;
      idpId?: string | null;
    },
  ) => {
    set($userDialogMode, 'edit');
    set($userDialogId, user.id);
    set($userFormData, {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber ?? undefined,
      timezone: user.timezone ?? undefined,
      idpId: user.idpId ?? undefined,
      organizationId: undefined,
    });
    set($userDialogOpen, true);
  },
);

// 🟠🟠🟠 ACTION -> CLOSE dialog
export const $closeUserDialog = atom(null, (_get, set) => {
  set($userDialogOpen, false);
  set($userFormData, getInitialFormData());
  set($userDialogId, null);
});

// 🟠🟠🟠 ACTION -> UPDATE form data
export const $updateUserFormData = atom(
  null,
  (get, set, update: Partial<UserFormData>) => {
    const currentData = get($userFormData);
    set($userFormData, {
      ...currentData,
      ...update,
    });
  },
);

// 🟠🟠🟠 ACTION -> SAVE user
export const $saveUser = atom(null, async (get, set) => {
  const mode = get($userDialogMode);
  const formData = get($userFormData);
  const userId = get($userDialogId);

  if (mode === 'edit' && userId) {
    await set($updateUser, {
      id: userId,
      ...formData,
    });
  } else {
    await set($createUser, {
      ...formData,
      timezone: ensureValue(formData.timezone),
    });
  }

  // Refresh user list and close dialog
  get($users).refetch();
  set($userDialogOpen, false);
  set($userFormData, getInitialFormData());
});

// 🟢🟢🟢 COMPUTED DATA -> form validation
export const $userFormValid = atom((get) => {
  const formData = get($userFormData);
  return !!(
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.email.trim() &&
    formData.email.includes('@') &&
    formData.timezone
  );
});

// 🟢🟢🟢 COMPUTED DATA -> is saving
export const $userDialogSaving = atom((get) => {
  const createStatus = get($createUser);
  const updateStatus = get($updateUser);
  const addToOrgStatus = get($addUserToOrganization);
  return (
    createStatus.isPending || updateStatus.isPending || addToOrgStatus.isPending
  );
});
